import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Layout,
  notification,
  Radio,
  Row,
  Select,
  Switch,
  Table,
  TableColumnsType,
  TableProps,
  TreeSelect
} from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { get, isEmpty, map, toNumber, includes } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import FormActionButton from "src/components/common/FormActionButton";
import { apiCaller, cancelListToken } from "src/util/apiCaller";
import { MessageStatusError } from "src/util/MessageStatus";

const FormItem = Form.Item;

const initialState = {
  userName: "",
  ten: "",
  hoLot: "",
  password: "",
  confirm: "",
  diaChi: "",
  dienThoai: "",
  emails: "",
  idNguoiDungKhac: undefined,
  isHoatDong: true
};

const listGioiTinh = [
  { label: "Nam", value: true },
  { label: "Nữ", value: false }
];

type TableRowSelection<T> = TableProps<T>["rowSelection"];

interface DataType {
  key: React.Key;
  tenQuyen: string;
}

interface DataTypeNhomTuLieu {
  key: React.Key;
  tenNhomTuLieu: string;
}

const NguoiDungForm = ({ status }: any) => {
  const { isActive } = status;
  const navigate = useNavigate();
  const location = useLocation();
  const [type, setType] = useState("new");
  const [fieldTouch, setFieldTouch] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const [loadingButtonClose, setLoadingButtonClose] = useState(false);
  const [form] = Form.useForm();
  const [dataChucVu, setDataChucVu] = useState([]);
  const [dataNguoiDung, setDataNguoiDung] = useState([]);
  const [dataVaiTro, setDataVaiTro] = useState([]);
  const [isLoadTable, setIsLoadTable] = useState<boolean>(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([""]);
  const [selectedRows, setSelectedRows] = useState([""]);
  const [gioiTinh, setGioiTinh] = useState(true);
  const [phongBanId, setPhongBanId] = useState(undefined);
  const [idChucVu, setIdChucVu] = useState(undefined);
  const [treeDataPB, setTreeDataPB] = useState([]);
  const [isLoadTableNhomTL, setIsLoadTableNhomTL] = useState<boolean>(false);
  const [dataNhomTuLieu, setDataNhomTuLieu] = useState([]);
  const [selectedRowKeysNhomTL, setSelectedRowKeysNhomTL] = useState<
    React.Key[]
  >([]);
  const [selectedRowsNhomTL, setSelectedRowsNhomTL] = useState<React.Key[]>([]);
  const {
    userName,
    ten,
    hoLot,
    diaChi,
    dienThoai,
    idNguoiDungKhac,
    isHoatDong
  } = initialState;
  const { validateFields, resetFields, setFieldsValue } = form;
  const { id } = useParams();

  const columns: TableColumnsType<DataType> = [
    { title: "Vai trò", dataIndex: "tenVaiTro", key: "tenVaiTro" }
  ];

  const columnsNhomTuLieu: TableColumnsType<DataTypeNhomTuLieu> = [
    { title: "Nhóm tài liệu", dataIndex: "tenNhomTuLieu", key: "tenNhomTuLieu" }
  ];

  useEffect(() => {
    if (isActive) {
      const load = () => {
        if (includes(location.pathname, "them-moi")) {
          setType("new");
        } else {
          setType("edit");
          getInfo();
        }
      };
      getListDataPhongBan();
      getListDataChucVu();
      getListDataNguoiDung();
      getListDataVaiTro();
      getListDataNhomTL();
      load();
    } else {
      navigate("/error/401");
    }
  }, []);

  useEffect(() => {
    return () => {
      cancelListToken([
        "gettreealluser",
        "listChucVu",
        "listNguoiDung",
        "listVaiTro",
        "infoNguoiDung"
      ]);
    };
  }, []);

  /**
   * findNodeById: Hàm tìm node có "id" tương ứng trong cây dữ liệu
   * @param {Array} data
   * @param {number} idToFind
   * @memberof NguoiDungForm
   */
  const findNodeById = useCallback((data: any, idToFind: any): any => {
    for (const node of data) {
      if (node.id === idToFind) {
        return node;
      }
      if (node.children && node.children.length > 0) {
        const foundNode = findNodeById(node.children, idToFind);
        if (foundNode) {
          return foundNode;
        }
      }
    }
    return null;
  }, []);

  /**
   * getListDataPhongBan: Lấy danh sách phòng ban
   * @memberof NguoiDungForm
   */
  const getListDataPhongBan = async () => {
    const configAPI = {
      url: `api/phongbans/gettreealluser`,
      method: "GET",
      value: null,
      apiType: "LIST",
      getName: "gettreealluser"
    };
    const response = await apiCaller(configAPI);
    try {
      if (
        response?.data &&
        (response.status === 200 || response.status === 201)
      ) {
        const data = get(response, "data", []);
        setTreeDataPB(data as any);
      } else {
        MessageStatusError(toNumber(response?.status));
      }
    } catch (err) {
      err;
    }
  };

  /**
   * getListDataChucVu: Lấy danh sách chức vụ
   * @memberof NguoiDungForm
   */
  const getListDataChucVu = async () => {
    setIsLoadTable(true);
    const configAPI = {
      url: `api/hethongchucvus?page=-1`,
      method: "GET",
      value: null,
      apiType: "LIST",
      getName: "listChucVu"
    } as any;
    const response = await apiCaller(configAPI);
    try {
      if (
        response?.data &&
        (response.status === 200 || response.status === 201)
      ) {
        const listChucVu = get(response.data, "content", []);
        setDataChucVu(listChucVu);
      } else {
        MessageStatusError(toNumber(response?.status));
      }
    } catch (err) {
      err;
    }
  };

  /**
   * getListDataNguoiDung: Lấy danh sách người dùng
   * @memberof NguoiDungForm
   */
  const getListDataNguoiDung = async () => {
    setIsLoadTable(true);
    const configAPI = {
      url: `api/nguoidungs?isHoatDong=1&page=-1`,
      method: "GET",
      value: null,
      apiType: "LIST",
      getName: "listNguoiDung"
    } as any;
    const response = await apiCaller(configAPI);
    try {
      if (
        response?.data &&
        (response.status === 200 || response.status === 201)
      ) {
        const listNguoiDung = get(response.data, "content", []);
        setDataNguoiDung(listNguoiDung);
      } else {
        MessageStatusError(toNumber(response?.status));
      }
    } catch (err) {
      err;
    }
  };

  /**
   * getListDataNguoiDung: Lấy danh sách vai trò
   * @memberof NguoiDungForm
   */
  const getListDataVaiTro = async () => {
    setIsLoadTable(true);
    const configAPI = {
      url: `api/vaitros`,
      method: "GET",
      value: null,
      apiType: "LIST",
      getName: "listVaiTro"
    } as any;
    const response = await apiCaller(configAPI);
    try {
      if (
        response?.data &&
        (response.status === 200 || response.status === 201)
      ) {
        const listVaiTro = get(response.data, "content", []).map(
          (item: { [x: string]: any; children: any }) => {
            const { children, ...rest } = item;
            return rest;
          }
        );
        setDataVaiTro(listVaiTro);
        setIsLoadTable(false);
      } else {
        MessageStatusError(toNumber(response?.status));
      }
    } catch (err) {
      err;
    }
  };

  /**
   * getListDataChucVu: Lấy danh sách chức vụ
   * @memberof NguoiDungForm
   */
  const getListDataNhomTL = async () => {
    setIsLoadTableNhomTL(true);
    const configAPI = {
      url: `api/nhomtulieus_all?page=0`,
      method: "GET",
      value: null,
      apiType: "LIST",
      getName: "listChucVu"
    } as any;
    const response = await apiCaller(configAPI);
    try {
      if (
        response?.data &&
        (response.status === 200 || response.status === 201)
      ) {
        const listChucVu = get(response.data, "content", []);
        setDataNhomTuLieu(listChucVu);
        setIsLoadTableNhomTL(false);
      } else {
        MessageStatusError(toNumber(response?.status));
        setIsLoadTableNhomTL(false);
      }
    } catch (err) {
      err;
    }
  };

  /**
   * getInfo : Lấy thông tin
   * @memberof  VaiTroForm
   */
  const getInfo = async () => {
    if (location.state && location.state.itemData) {
      // Nếu có dữ liệu truyền sang
      const res = location.state.itemData;
      setFieldsValue({
        nguoidung: res
      });
      setSelectedRowKeys(
        res.vaiTros ? res.vaiTros.map((item: any) => item.id) : []
      );
      setSelectedRows(res.vaiTros);
      setSelectedRowKeysNhomTL(
        res.nhomTuLieus ? res.nhomTuLieus.map((item: any) => item.id) : []
      );
      setSelectedRowsNhomTL(res.nhomTuLieus);
      setFieldsValue({
        nguoidung: {
          phongBanId: !isEmpty(res.phongBan) ? res.phongBan.id : []
        }
      });
      setFieldsValue({
        nguoidung: {
          idChucVu: !isEmpty(res.chucVus)
            ? map(res.chucVus, (item) => (item = item.id))
            : []
        }
      });
      setPhongBanId(res.phongBan.id);
    } else {
      const configAPI = {
        url: `api/nguoidungs/${id}`,
        method: "GET",
        value: "",
        apiType: "DETAIL",
        getName: "infoNguoiDung"
      };
      const response = await apiCaller(configAPI);
      try {
        if (
          response?.data &&
          (response.status === 200 || response.status === 201)
        ) {
          setSelectedRowKeys(
            response.data.vaiTros
              ? response.data.vaiTros.map((item: any) => item.id)
              : []
          );
          setSelectedRows(response.data.vaiTros);
          setSelectedRowKeysNhomTL(
            response.data.nhomTuLieus
              ? response.data.nhomTuLieus.map((item: any) => item.id)
              : []
          );
          setSelectedRowsNhomTL(response.data.nhomTuLieus);
          setFieldsValue({
            nguoidung: {
              phongBanId: !isEmpty(response.data.phongBan)
                ? response.data.phongBan.id
                : []
            }
          });
          setFieldsValue({
            nguoidung: {
              idChucVu: !isEmpty(response.data.chucVus)
                ? map(response.data.chucVus, (item) => (item = item.id))
                : []
            }
          });
          setFieldsValue({
            nguoidung: response.data
          });
        } else {
          MessageStatusError(toNumber(response?.status));
        }
      } catch (error) {
        error;
        // Xử lý lỗi khi lưu dữ liệu không thành công
      }
    }
  };

  /**
   * onChangeDonVi: Chọn đơn vị
   * @param {number} e
   * @memberof NguoiDungForm
   */
  const onChangeDonVi = (e: any) => {
    setPhongBanId(e);
  };

  /**
   * onChange: Chọn hàng table
   * @param {Array} newSelectedRowKeys
   * @param {Array} selectedRows
   * @memberof NguoiDungForm
   */
  const onChange = (newSelectedRowKeys: any, selectedRows: any) => {
    setSelectedRowKeys(newSelectedRowKeys);
    setSelectedRows(selectedRows);
  };

  /**
   * onSelect: Chọn hàng table
   * @param {Array} selected
   * @param {Array} selectedRows
   * @memberof NguoiDungForm
   */
  const onSelect = (selected: any, selectedRows: any) => {
    setSelectedRowKeys(selected);
    setSelectedRows(selectedRows);
  };

  /**
   * onChange: Chọn hàng table
   * @param {Array} newSelectedRowKeys
   * @param {Array} selectedRows
   * @memberof NguoiDungForm
   */
  const onChangeNhomTL = (newSelectedRowKeys: any, selectedRows: any) => {
    setSelectedRowKeysNhomTL(newSelectedRowKeys);
    setSelectedRowsNhomTL(selectedRows);
  };

  /**
   * onSelect: Chọn hàng table
   * @param {Array} selected
   * @param {Array} selectedRows
   * @memberof NguoiDungForm
   */
  const onSelectNhomTL = (selected: any, selectedRows: any) => {
    setSelectedRowKeysNhomTL(selected);
    setSelectedRowsNhomTL(selectedRows);
  };

  /**
   * rowSelection: Chọn trên bảng
   * @param {object} selectedRowKeys
   * @param {object} selectedRows
   * @memberof VaiTroForm
   */
  const rowSelection: TableRowSelection<DataType> = {
    selectedRowKeys,
    hideSelectAll: false,
    onChange: onChange,
    onSelect: onSelect
  };

  /**
   * rowSelection: Chọn trên bảng nhóm tài liệu
   * @param {object} selectedRowKeys
   * @param {object} selectedRows
   * @memberof VaiTroForm
   */
  const rowSelectionNhomTL: TableRowSelection<DataTypeNhomTuLieu> = {
    selectedRowKeys: selectedRowKeysNhomTL,
    hideSelectAll: false,
    onChange: onChangeNhomTL,
    onSelect: onSelectNhomTL
  };

  /**
   *validatePhoneNumber : Hàm validate số điện thoại
   *
   * @param {string} _ chính là Rules, nhưng ko sử dụng
   * @param {string} value
   * @memberof NguoiDungForm
   */
  const validatePhoneNumber = (_: any, value: any) => {
    if (!value) {
      return Promise.resolve();
    }
    // Kiểm tra xem giá trị nhập vào chỉ chứa số và có đúng 10 hoặc 11 chữ số
    if (!/^\d{10}$/.test(value) && !/^\d{11}$/.test(value)) {
      return Promise.reject(
        new Error("Số điện thoại phải có 10 hoặc 11 chữ số và chỉ được nhập số")
      );
    }
    return Promise.resolve();
  };

  /**
   * validateNoWhitespace : Hàm validate khoảng trắng
   * @param {string} _ chính là Rules, nhưng ko sử dụng
   * @param {string} value
   * @memberof NguoiDungForm
   */
  const validateNoWhitespace = (_: any, value: any) => {
    if (value && /\s/.test(value)) {
      return Promise.reject(new Error("Không được nhập có khoảng trắng"));
    }
    return Promise.resolve();
  };

  /**
   * validateNoVietnamese : Hàm validate nhập tiếng việt
   * @param {string} _ chính là Rules, nhưng ko sử dụng
   * @param {string} value
   * @memberof NguoiDungForm
   */
  const validateNoVietnamese = (_: any, value: any) => {
    if (value && /[\u0080-\uFFFF]/.test(value)) {
      return Promise.reject(
        new Error("Vui lòng nhập tiếng việt không dấu với tên đăng nhập")
      );
    }
    return Promise.resolve();
  };

  /**
   *
   * validateEmail  : Hàm validate nhập đúng email
   * @param {string} _ chính là Rules, nhưng ko sử dụng
   * @param {string} value
   * @memberof NguoiDungForm
   */
  const validateEmail = (_: any, value: any) => {
    if (value && !/^[a-zA-Z0-9._%+-]+@quangnam\.gov\.vn$/.test(value)) {
      return Promise.reject(
        new Error(
          "Vui lòng nhập đúng định dạng email (ví dụ : nguyenvana@quangnam.gov.vn)"
        )
      );
    }
    return Promise.resolve();
  };

  /**
   * treeDataSelect: Vòng lặp tìm treeData
   * @memberof NguoiDungForm
   */
  const treeDataSelect = useMemo(() => {
    const loop = (data: any) =>
      data?.map((item: any) => {
        if (item.children) {
          return {
            title: item.tenDonVi,
            value: item.id,
            key: item.id,
            children: loop(item.children)
          };
        }
        return {
          title: item.tenDonVi,
          value: item.id,
          key: item.id
        };
      });
    return loop(treeDataPB);
  }, [treeDataPB]);

  /**
   * onChangeNguoiDung: Chọn vai trò người dùng khác
   * @param {int} id
   * @memberof NguoiDungForm
   */
  const onChangeNguoiDung = async (id: number) => {
    if (id) {
      const configNguoiDung = {
        url: `api/nguoidungs/${id}`,
        method: "GET",
        value: null,
        apiType: "LIST"
      };

      const response = await apiCaller(configNguoiDung);
      try {
        if (
          response?.data &&
          (response.status === 200 || response.status === 201)
        ) {
          const listVaiTroNguoiDung = map(response.data.vaiTros, (vt) => {
            return vt.id;
          });
          const listNhomTuLieu = map(response.data.nhomTuLieus, (nhom) => {
            return nhom.id;
          });
          setSelectedRowKeys(listVaiTroNguoiDung);
          setSelectedRows(response.data.vaiTros);
          setSelectedRowKeysNhomTL(listNhomTuLieu);
          setSelectedRowsNhomTL(response.data.nhomTuLieus);
        } else {
          MessageStatusError(toNumber(response?.status));
        }
      } catch (error) {
        error;
        // Xử lý lỗi khi lưu dữ liệu không thành công
      }
    }
  };

  /**
   * onChangeGioiTinh: Chọn giới tính
   * @param {number} e
   * @memberof NguoiDungForm
   */
  const onChangeGioiTinh = (e: any) => {
    setGioiTinh(e.target.value);
  };

  /**
   * onChangeChucVu: Chọn mã chức vụ
   * @param {number} e
   * @memberof NguoiDungForm
   */
  const onChangeChucVu = (value: any) => {
    setIdChucVu(value);
  };

  /**
   * goBack: quay lại trang danh sách
   * @memberof NguoiDungForm
   */
  const goBack = () => {
    navigate("/quan-tri/nguoi-dung");
  };

  /**
   * save: Lưu
   * @memberof NguoiDungForm
   */
  const save = () => {
    validateFields()
      .then((values) => {
        setLoadingButton(true);
        if (selectedRowKeysNhomTL.length === 0) {
          setLoadingButton(false);
          notification.error({
            message: "Thông báo",
            duration: 4,
            description: "Lưu người dùng phải chọn nhóm tài liệu"
          });
        } else {
          saveData(values.nguoidung, false);
        }
      })
      .catch(() => {
        setLoadingButton(false);
      });
  };

  /**
   * saveAndClose: Lưu và đóng
   * @memberof VaiTroForm
   */
  const saveAndClose = () => {
    validateFields()
      .then((values) => {
        setLoadingButton(true);
        saveData(values.nguoidung, true);
      })
      .catch(() => {
        setLoadingButton(false);
      });
  };

  /**
   * saveData: Lưu dữ liệu
   * @param {object} values
   * @param {boolean} saveQuit
   * @memberof NguoiDungForm
   */
  const saveData = async (values: any, saveQuit = false) => {
    const newData = values;
    setFieldsValue(values);
    newData.hoVaTen = `${newData.hoLot} ${newData.ten}`;
    newData.emails = newData.userName;
    newData.userName = values.userName;
    newData.idCoQuan = phongBanId;
    newData.phongBanId = phongBanId;
    newData.avatar = "";
    const listItemCV: any[] = [];
    map(newData.idChucVu, (item) =>
      map(dataChucVu, (i: any) => {
        if (i.id === item) {
          listItemCV.push(i.tenChucVu);
        }
      })
    );
    newData.lstchucVu = listItemCV.join(",");
    newData.lstvaiTro =
      selectedRows &&
      selectedRows.map((item: any) => item.tenVaiTro).toString();
    newData.lstNhomTuLieu =
      selectedRowsNhomTL &&
      selectedRowsNhomTL.map((item: any) => item.tenNhomTuLieu).toString();
    newData.isHoatDong = true;
    try {
      let configAPI;
      if (type === "new") {
        configAPI = {
          url: `api/nguoidungs/create`,
          method: "POST",
          data: newData,
          apiType: "ADD",
          isShowMessage: true
        };
      } else {
        newData.id = id;
        configAPI = {
          url: `api/nguoidungs/${id}`,
          method: "PUT",
          data: newData,
          apiType: "EDIT",
          isShowMessage: true
        };
      }

      const response = await apiCaller(configAPI);
      if (response?.status === 200 || response?.status === 201) {
        if (saveQuit) {
          goBack();
          setLoadingButtonClose(false);
        } else {
          if (type === "new") {
            resetFields();
          }
          setFieldTouch(false);
          setLoadingButton(false);
        }
      } else {
        MessageStatusError(toNumber(response?.status));
        setLoadingButton(false);
      }
    } catch (error) {
      setLoadingButton(false);
    }
  };

  const formTitle =
    type === "new" ? "Thêm mới người dùng" : "Chỉnh sửa người dùng";
  return (
    <Layout>
      <div>
        <Col md={12} style={{ display: "ruby" }}>
          <Button type="text" onClick={goBack}>
            <ArrowLeftOutlined />
          </Button>
          <h2 style={{ color: "black", fontSize: 20, marginTop: 10 }}>
            {formTitle}
          </h2>
        </Col>
      </div>
      <Card>
        <Form
          className="form-container wrapper-content-inner"
          layout="vertical"
          form={form}
          name="vaitro-control"
          onFieldsChange={() => setFieldTouch(true)}
        >
          <Row gutter={24}>
            <Col md={24} xs={24}>
              <FormItem
                className="ant-input-group"
                label="Tên đăng nhập"
                name={["nguoidung", "userName"]}
                rules={[
                  { required: true, message: "Cần điền tên đăng nhập" },
                  {
                    type: "string",
                    min: 3,
                    message: "Tên đăng nhập từ 3 ký tự trở lên"
                  },
                  { validator: validateNoWhitespace },
                  { validator: validateNoVietnamese },
                  { validator: validateEmail }
                ]}
                initialValue={userName}
              >
                <Input
                  placeholder="Nhập tên đăng nhập..."
                  name="userNameKeyword"
                  disabled={type === "edit"}
                />
              </FormItem>
            </Col>
            <Col md={8} xs={24}>
              <FormItem
                className="ant-input-group"
                label="Họ lót"
                name={["nguoidung", "hoLot"]}
                rules={[
                  { required: true, message: "Cần điền họ lót" },
                  { type: "string", max: 255 }
                ]}
                initialValue={hoLot}
              >
                <Input
                  name="hoLot"
                  placeholder="Nhập họ lót"
                  disabled={type === "edit"}
                />
              </FormItem>
            </Col>
            <Col md={8} xs={24}>
              <FormItem
                className="ant-input-group"
                label="Tên"
                name={["nguoidung", "ten"]}
                rules={[
                  { required: true, message: "Cần điền tên" },
                  { type: "string", max: 255 }
                ]}
                initialValue={ten}
              >
                <Input
                  name="ten"
                  placeholder="Nhập tên"
                  disabled={type === "edit"}
                />
              </FormItem>
            </Col>
            <Col md={8} xs={24}>
              <FormItem
                className="ant-input-group"
                label="Giới tính"
                name={["nguoidung", "gioiTinh"]}
                initialValue={gioiTinh}
              >
                <Radio.Group
                  options={listGioiTinh}
                  name="gioiTinh"
                  value={gioiTinh}
                  onChange={onChangeGioiTinh}
                />
              </FormItem>
            </Col>
            <Col md={8} xs={24}>
              <FormItem
                className="ant-input-group"
                label="Phòng ban"
                name={["nguoidung", "phongBanId"]}
                rules={[{ required: true, message: "Cần chọn phòng ban !" }]}
                initialValue={phongBanId}
              >
                <TreeSelect
                  showSearch
                  placeholder="Vui lòng chọn"
                  onChange={onChangeDonVi}
                  treeData={treeDataSelect}
                  treeNodeFilterProp="title"
                  style={{ width: "100%" }}
                  dropdownStyle={{ overflow: "auto" }}
                />
              </FormItem>
            </Col>
            <Col md={8} xs={24}>
              <FormItem
                className="ant-input-group"
                label="Chức vụ"
                name={["nguoidung", "idChucVu"]}
                rules={[{ required: true, message: "Cần chọn chức vụ !" }]}
                initialValue={idChucVu || undefined}
              >
                <Select
                  allowClear
                  showSearch
                  mode="multiple"
                  onChange={onChangeChucVu}
                  placeholder="Chọn chức vụ"
                  style={{ width: "100%" }}
                  options={dataChucVu}
                  optionFilterProp="tenChucVu"
                  filterOption={(input, option: any) =>
                    (option?.tenChucVu ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  fieldNames={{ label: "tenChucVu", value: "id" }}
                ></Select>
              </FormItem>
            </Col>
            <Col md={8} xs={24}>
              <FormItem
                label="Hoạt động"
                name={["vaitro", "isHoatDong"]}
                valuePropName="checked"
                initialValue={isHoatDong}
              >
                <Switch />
              </FormItem>
            </Col>
            <Col md={8} xs={24}>
              <FormItem
                className="ant-input-group"
                label="Địa chỉ"
                name={["nguoidung", "diaChi"]}
                initialValue={diaChi}
                rules={[
                  {
                    whitespace: true
                    // message: messages.ERROR_REQUIRED
                  },
                  {
                    max: 255
                    // message: messages.ERROR_MAX.replace('{value}', 255)
                  }
                ]}
              >
                <Input className="input-item" placeholder="Nhập địa chỉ" />
              </FormItem>
            </Col>
            <Col md={8} xs={24}>
              <FormItem
                className="ant-input-group"
                label="Số địa thoại"
                name={["nguoidung", "dienThoai"]}
                initialValue={dienThoai}
                rules={[
                  {
                    whitespace: true
                    // message: messages.ERROR_REQUIRED
                  },
                  {
                    max: 255
                    // message: messages.ERROR_MAX.replace('{value}', 255)
                  },
                  { validator: validatePhoneNumber }
                ]}
              >
                <Input
                  className="input-item"
                  placeholder="Nhập số điện thoại"
                />
              </FormItem>
            </Col>
            <Col md={8} xs={24}>
              <FormItem
                className="QTI-FormItemSelect"
                label="Sao chép vai trò của :"
                name={["nguoidung", "idNguoiDungKhac"]}
                initialValue={idNguoiDungKhac}
              >
                <Select
                  allowClear
                  showSearch
                  // name="idNguoiDungKhac"
                  value={idNguoiDungKhac}
                  optionFilterProp="children"
                  onChange={onChangeNguoiDung}
                  placeholder="Chọn người dùng sao chép vai trò"
                  style={{ width: "100%" }}
                  options={dataNguoiDung}
                  fieldNames={{ label: "hoVaTen", value: "id" }}
                ></Select>
              </FormItem>
            </Col>
            <Col span={24}>
              <FormItem
                style={{ marginTop: "15px" }}
                initialValue={selectedRowKeys}
              >
                <Table
                  bordered
                  rowSelection={rowSelection}
                  expandable={{
                    rowExpandable: () => false
                  }}
                  columns={columns}
                  rowKey={"id"}
                  dataSource={dataVaiTro}
                  rowClassName={(record, index) =>
                    index % 2 === 0 ? "qti-table-row-background" : ""
                  }
                  pagination={false}
                  loading={isLoadTable}
                  size="middle"
                  className="qti-table-wrapper gx-table-responsive"
                />
              </FormItem>
            </Col>
            <Col span={24}>
              <FormItem
                style={{ marginTop: "15px" }}
                initialValue={selectedRowKeysNhomTL}
              >
                <Table
                  bordered
                  rowSelection={rowSelectionNhomTL}
                  columns={columnsNhomTuLieu}
                  rowKey={"id"}
                  dataSource={dataNhomTuLieu}
                  rowClassName={(record, index) =>
                    index % 2 === 0 ? "qti-table-row-background" : ""
                  }
                  pagination={false}
                  loading={isLoadTableNhomTL}
                  size="middle"
                  className="qti-table-wrapper gx-table-responsive"
                />
              </FormItem>
            </Col>
          </Row>
          <hr />
          <FormActionButton
            goBack={goBack}
            saveAndClose={saveAndClose}
            disabled={fieldTouch}
            save={save}
            type={type}
            loadingButton={loadingButton}
            loadingButtonClose={loadingButtonClose}
          />
        </Form>
      </Card>
    </Layout>
  );
};

export default NguoiDungForm;
